// First screen of every page load. Two jobs at once:
//
//   1. Get the user's first interaction. Browsers won't let an
//      AudioContext play until there's user activation, so the splash's
//      perfectly-timed music cue (1.0s) needs *some* tap/click to have
//      already happened. Picking a flag (or hitting Continue) is that
//      tap, and we resume the menu's AudioContext synchronously inside
//      the same gesture-handling task.
//
//   2. Pre-warm the asset cache. The moment the user touches anything,
//      we fire `preloader.startAll()` so all tiers (1-5) are already
//      downloading while the user reads the flag grid. Continue then
//      blocks on tier 1 (the tutorial asset set) so a fresh player can
//      flow straight into a ready tutorial; if not yet warm, a small
//      inline "Loading..." replaces the Continue label until it is.
//
// On a return visit the stored locale (localStorage `lang`) is pre-
// selected and Continue is auto-focused, so the user can just hit Enter
// or tap once and proceed. The grid stays visible underneath in case
// they want to change.

import { menuAudio } from './MenuAudio';
import { preloader } from '../assets/Preloader';
import { MenuNav, type MenuItem } from './MenuNav';
import { StageScaler } from './StageScaler';
import {
  SUPPORTED_LANGUAGES, getLocale, setLocale, t,
  type SupportedLanguage
} from '../i18n';
import { isFullscreen, enterFullscreen } from './fullscreen';

const STYLES_ID = 'lang-select-styles';

// Card design size — the StageScaler scales the whole card uniformly to
// fit the viewport, exactly like HomeScreen and SettingsScreen. Picked so
// the flag list reads comfortably on a 1080p desktop (1× scale, 720px-wide
// column) while a mobile portrait viewport ends up scaled to a height
// that shows multiple rows at once instead of one.
const CARD_W = 720;
const CARD_H = 1080;

const STYLES = `
@import url('https://fonts.googleapis.com/css2?family=Chewy&display=swap');

/* Full-viewport backdrop. Holds the scaled .card centered + letterboxed.
   Matches the SettingsScreen modal pattern so mobile viewports get a
   uniform scale-down instead of cropping the list to a single row. */
#lang-select {
  position: fixed; inset: 0;
  z-index: 300;
  background: #02050E;
  font-family: 'Chewy', ui-sans-serif, system-ui, sans-serif;
  color: #EEF4F6;
  opacity: 0;
  transition: opacity 320ms ease;
}
#lang-select.shown { opacity: 1; }
#lang-select.closing { opacity: 0; pointer-events: none; }

/* 1:1 design-pixel column. Width / height set inline by JS;
   StageScaler writes the transform every frame the viewport resizes.
   Side padding is zero so the flag column matches the original 720px
   width when the card is at 1× scale. */
#lang-select .card {
  position: absolute; top: 50%; left: 50%;
  transform-origin: center center;
  display: flex; flex-direction: column; align-items: center;
  gap: 18px;
  padding: 56px 0 48px;
  box-sizing: border-box;
}

#lang-select .title {
  font-size: 32px;
  line-height: 1;
  letter-spacing: 0.03em;
  text-shadow: 0 -2px 0 rgba(0, 60, 90, 0.45);
  margin: 0;
  flex: 0 0 auto;
  text-align: center;
}

#lang-select .grid {
  display: flex; flex-direction: column;
  gap: 10px;
  width: 100%;
  flex: 1 1 auto;
  min-height: 0;
  overflow-y: auto;
  /* Vertical padding pushes the first/last item below/above the mask fade
     zone, so when scrolled to either end the items render fully opaque
     instead of disappearing into the gradient. The padding is part of the
     scroll container, so as the user scrolls past the ends the items
     correctly start fading — the gradient only "eats" content when there
     is content to fade. */
  padding: 32px 14px 32px 8px;
  /* Fade window: 24px at top/bottom indicates more rows exist
     below/above the viewport. Sits inside the padding so resting items
     are never inside the fade. */
  mask-image: linear-gradient(180deg,
    transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%);
}
#lang-select .grid::-webkit-scrollbar { width: 8px; }
#lang-select .grid::-webkit-scrollbar-track { background: transparent; }
#lang-select .grid::-webkit-scrollbar-thumb {
  background: rgba(255,255,255,0.18); border-radius: 4px;
}

#lang-select .flag {
  display: flex; flex-direction: row; align-items: center; gap: 22px;
  width: 100%;
  padding: 16px 24px;
  background: rgba(255,255,255,0.05);
  border: 2px solid rgba(255,255,255,0.10);
  border-radius: 14px;
  color: #EEF4F6;
  font-family: 'Chewy', sans-serif;
  font-size: 45px;
  cursor: pointer;
  text-align: left;
  transition: background 140ms ease, border-color 140ms ease;
  -webkit-text-stroke: 0;
  flex: 0 0 auto;
}
#lang-select .flag:hover {
  background: rgba(255,255,255,0.10);
  border-color: rgba(255,255,255,0.25);
}
#lang-select .flag.selected {
  background: rgba(250, 156, 34, 0.18);
  border-color: #FA9C22;
}
#lang-select .flag.focused {
  outline: 3px solid #FA9C22;
  outline-offset: 2px;
}
#lang-select .flag .emoji {
  font-size: 65px;
  line-height: 1;
  flex: 0 0 auto;
}
#lang-select .flag .name {
  font-size: 45px;
  letter-spacing: 0.02em;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  flex: 1 1 auto;
}

#lang-select .continue {
  margin-top: 4px;
  flex: 0 0 auto;
  border: 0; cursor: pointer;
  font-family: 'Chewy', sans-serif;
  font-size: 28px;
  letter-spacing: 0.04em;
  color: #FFFFFF;
  -webkit-text-stroke: 3px #356B8C;
  paint-order: stroke fill;
  background: linear-gradient(180deg, #7DD2F5 0%, #3DA6FF 100%);
  box-shadow: 0 4px 0 #256AA0, 0 8px 18px rgba(0,0,0,0.3);
  border-radius: 16px;
  padding: 14px 36px;
  min-width: 220px;
  transition: filter 120ms ease, transform 120ms ease;
}
#lang-select .continue:hover { filter: brightness(1.05); }
#lang-select .continue.focused {
  outline: 4px solid rgba(255,255,255,0.6);
  outline-offset: 3px;
}
#lang-select .continue:active { transform: translateY(2px); }
#lang-select .continue.loading {
  filter: saturate(0.5) brightness(0.85);
  cursor: progress;
}

[dir="rtl"] #lang-select .flag .name { direction: rtl; }
`;

// Same capture-phase audio-unlock listener pattern that SplashScreen uses.
// We arm them here so the very first tap/key inside the language screen
// resumes the AudioContext, then hand the splash a fresh set on construction
// (its listeners self-remove on the first hit too).
const AUDIO_UNLOCK_EVENTS = ['pointerdown', 'pointerup', 'touchstart', 'touchend', 'mousedown', 'click', 'keydown'] as const;
const AUDIO_UNLOCK_OPTS: AddEventListenerOptions = { capture: true, passive: true };

export interface LanguageSelectOptions {
  /** Pre-selected locale code (typically the value returned by
   *  `getStoredLocale() ?? detectBrowserLocale()`). The grid will
   *  highlight this entry and Continue will be the initially-focused
   *  item, so a return visitor can advance with a single Enter press. */
  initialCode: string;
}

export class LanguageSelectScreen {
  private root: HTMLElement;
  private cardEl: HTMLElement;
  private scaler: StageScaler;
  private styleEl: HTMLStyleElement | null = null;
  private nav: MenuNav | null = null;
  private flagButtons: HTMLButtonElement[] = [];
  private continueBtn: HTMLButtonElement;
  private continueIndex = 0;
  private picked: string;
  private resolveShow: ((picked: string) => void) | null = null;
  private interactionStarted = false;
  private disposed = false;

  constructor(opts: LanguageSelectOptions) {
    this.picked = SUPPORTED_LANGUAGES.find((l) => l.code === opts.initialCode)?.code
      ?? SUPPORTED_LANGUAGES[0]!.code;

    this.ensureStyles();
    this.root = document.createElement('div');
    this.root.id = 'lang-select';
    this.root.innerHTML = this.renderDom();
    document.body.appendChild(this.root);

    this.cardEl = this.root.querySelector('.card') as HTMLElement;
    this.cardEl.style.width = `${CARD_W}px`;
    this.cardEl.style.height = `${CARD_H}px`;
    this.scaler = new StageScaler({
      stageEl: this.cardEl,
      designW: CARD_W,
      designH: CARD_H,
      // Don't blow the column up past 1× on a giant monitor — at 720×1080
      // it's already wide enough that a 4K scale-up would feel oversized.
      maxScale: 1.0
    });
    this.scaler.start();

    this.flagButtons = Array.from(this.root.querySelectorAll<HTMLButtonElement>('.flag'));
    this.continueBtn = this.root.querySelector('.continue') as HTMLButtonElement;

    // Arm the capture-phase audio unlock + preload trigger on the very
    // first interaction. Removed after the first hit because a single
    // resume() is all the browser needs.
    for (const ev of AUDIO_UNLOCK_EVENTS) {
      addEventListener(ev, this.onFirstInteraction, AUDIO_UNLOCK_OPTS);
    }

    requestAnimationFrame(() => this.root.classList.add('shown'));
  }

  /** Show the screen and resolve with the picked locale once the user
   *  hits Continue and tier-1 assets are warm. */
  show(): Promise<string> {
    return new Promise<string>((resolve) => {
      this.resolveShow = resolve;
      this.installNav();
    });
  }

  private renderDom(): string {
    const flags = SUPPORTED_LANGUAGES.map((l) => this.renderFlag(l)).join('');
    return `
      <div class="card">
        <h1 class="title">${escapeHtml(t('language.title'))}</h1>
        <div class="grid" role="listbox">${flags}</div>
        <button type="button" class="continue">${escapeHtml(t('language.continue'))}</button>
      </div>
    `;
  }

  private renderFlag(l: SupportedLanguage): string {
    const sel = l.code === this.picked ? ' selected' : '';
    return `
      <button type="button" class="flag${sel}" data-code="${escapeAttr(l.code)}" role="option" aria-selected="${l.code === this.picked}">
        <span class="emoji" aria-hidden="true">${l.flag}</span>
        <span class="name">${escapeHtml(l.name)}</span>
      </button>
    `;
  }

  private installNav(): void {
    // Single-column vertical list: flags stack one per row, Continue is
    // the row directly below the last flag. Up/Down moves through the
    // list; Enter activates. MenuNav (with scrollBehavior:'auto') brings
    // the focused row into view so keyboard / controller users never lose
    // sight of the highlighted entry — and 'auto' means the rapid focus
    // moves from hold-to-repeat don't queue overlapping smooth scrolls.
    const items: MenuItem[] = this.flagButtons.map((btn, i) => ({
      el: btn,
      row: i,
      col: 0,
      onActivate: () => {
        const code = btn.dataset.code!;
        this.selectFlag(code);
        // After picking a language, jump focus to Continue — a click or
        // 'A' on a flag is the user's "this one" gesture, and the obvious
        // next step is to advance. Saves a redundant arrow-down + Enter.
        this.nav?.focusIndex(this.continueIndex, /* fromInput */ false);
      }
    }));
    this.continueIndex = items.length;
    items.push({
      el: this.continueBtn,
      row: this.flagButtons.length,
      col: 0,
      onActivate: () => { void this.commit(); }
    });
    // Default focus on Continue so a returning user (stored locale present
    // via ?lang=…) can just press Enter / A to advance. Hold-to-repeat is
    // on so a controller stick or held arrow scrolls through the ~40
    // languages without one-flick-per-row.
    this.nav = new MenuNav({
      items,
      initialIndex: items.length - 1,
      holdToRepeat: true,
      scrollBehavior: 'auto'
    });
    this.nav.mount();

    // On mount, scroll the pre-selected flag into view so a returning
    // user can see their stored choice highlighted in the list, even
    // if it sits below the visible portion of the scroll window.
    const sel = this.flagButtons.find((b) => b.dataset.code === this.picked);
    if (sel) sel.scrollIntoView({ block: 'center', behavior: 'auto' });
  }

  private selectFlag(code: string): void {
    this.picked = code;
    for (const btn of this.flagButtons) {
      const isPicked = btn.dataset.code === code;
      btn.classList.toggle('selected', isPicked);
      btn.setAttribute('aria-selected', String(isPicked));
    }
    // Apply the locale immediately so the title / Continue label re-render
    // in the picked language as instant feedback. Stored to localStorage
    // here too, so a F5 mid-pick remembers the latest choice.
    setLocale(code);
    // Start warming this locale's tutorial narration .ogg files. English
    // is already in tier 1 of the static manifest; everything else is
    // demand-loaded the moment its flag is touched. Idempotent + no-op
    // for locales without recorded narration.
    preloader.preloadLocaleTutorialNarration(code);
    const titleEl = this.root.querySelector('.title') as HTMLElement | null;
    if (titleEl) titleEl.textContent = t('language.title');
    if (!this.continueBtn.classList.contains('loading')) {
      this.continueBtn.textContent = t('language.continue');
    }
  }

  /** First user interaction with the language screen — could be a tap,
   *  click, or keypress on anything. We use it to (a) resume the
   *  AudioContext while we still have user activation in scope, and
   *  (b) kick off the full asset preload manifest so tiers 2-5 download
   *  while the user reads the flag grid. */
  private onFirstInteraction = (): void => {
    if (this.interactionStarted) return;
    this.interactionStarted = true;
    menuAudio.resume();
    preloader.startAll();
    this.removeAudioUnlockListeners();
  };

  private removeAudioUnlockListeners(): void {
    for (const ev of AUDIO_UNLOCK_EVENTS) {
      removeEventListener(ev, this.onFirstInteraction, AUDIO_UNLOCK_OPTS);
    }
  }

  /** User hit Continue. Wait for tier-1 assets (the tutorial set) to be
   *  cache-warm before handing off to the splash so a fresh player flows
   *  straight into a ready tutorial. If already cached (warm visit) this
   *  is instant; if not, the button shows a brief "Loading..." while we
   *  wait. */
  private async commit(): Promise<void> {
    if (this.disposed) return;
    setLocale(this.picked);
    // Defensive: in the unusual case where the user hit Enter on the
    // Continue button without ever moving focus to a flag (so
    // onFirstInteraction fired on the keydown, but the keydown was
    // bubbled-not-captured? unlikely), make sure preload is started.
    if (!this.interactionStarted) this.onFirstInteraction();

    // Continue is the player's first deliberate "go" — fire the
    // requestFullscreen call synchronously inside this user-activation
    // window. Awaiting would push it past the activation cutoff in some
    // browsers, so we fire-and-forget. Gamepad-only activation will
    // silently fail; the user can flip it on later from Graphics.
    if (!isFullscreen()) void enterFullscreen();

    const tier1 = preloader.awaitTier(1);
    let stillLoading = true;
    // Only show the loading state if tier 1 doesn't resolve within a
    // perceptible threshold — avoids a flicker of "Loading..." on warm
    // cache. 80ms is below the typical 100ms "instant" threshold.
    const maybeShowLoading = setTimeout(() => {
      if (!stillLoading || this.disposed) return;
      this.continueBtn.classList.add('loading');
      this.continueBtn.textContent = t('language.loading');
    }, 80);
    await tier1;
    stillLoading = false;
    clearTimeout(maybeShowLoading);

    if (this.disposed) return;
    const r = this.resolveShow;
    this.resolveShow = null;
    if (r) r(this.picked);
  }

  /** Fade out and detach. The splash will reattach its own audio-unlock
   *  listeners; ours have already self-removed after the first hit. */
  dispose(): void {
    if (this.disposed) return;
    this.disposed = true;
    this.removeAudioUnlockListeners();
    this.nav?.dispose();
    this.nav = null;
    this.scaler.stop();
    this.root.classList.add('closing');
    setTimeout(() => this.root.remove(), 320);
  }

  private ensureStyles(): void {
    if (document.getElementById(STYLES_ID)) return;
    this.styleEl = document.createElement('style');
    this.styleEl.id = STYLES_ID;
    this.styleEl.textContent = STYLES;
    document.head.appendChild(this.styleEl);
  }
}

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
function escapeAttr(s: string): string { return escapeHtml(s); }

void getLocale;
